import { toUpper } from 'lodash';
import pluralize from 'pluralize';
import { type MouseEvent, memo, type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import { type PublicClip } from '@air/api';
import { Comment, Ellipsis, Stack } from '@air/next-icons';
import { Spinner } from '@air/primitive-spinner';
import { type BaseCustomField, type Clip, ClipType } from '@air/types';
import { formatBytes } from '@air/utilities';
import { ClipUtils, isPdfRenderedExtension } from '@air/utils-clips';
import { cn } from '@air/utils-tailwind';

import { CardAction } from '~/components/CardAction';
import { CustomFieldsDisplay } from '~/components/Gallery/CustomFieldsDisplay';
import { GalleryCardCustomFieldsAction } from '~/components/Gallery/GalleryCardCustomFieldsAction';
import { GalleryItemType } from '~/components/Gallery/types';
import { getAssetCustomFields } from '~/components/Gallery/utils';
import { VideoScrubbableThumbnail } from '~/components/VideoScrubbableThumbnail';
import { VideoStaticThumbnail } from '~/components/VideoStaticThumbnail';
import { GalleryAssetMenu } from '~/components/v2/components/shared/gallery/GalleryAssetMenu';
import { GalleryAssetShortInfo } from '~/components/v2/components/shared/gallery/GalleryAssetShortInfo';
import { GalleryAssetThumbnail } from '~/components/v2/components/shared/gallery/GalleryAssetThumbnail';
import { GalleryAssetTitle } from '~/components/v2/components/shared/gallery/GalleryAssetTitle';
import {
  GallerySelectableCard,
  type GallerySelectableCardProps,
} from '~/components/v2/components/shared/gallery/GallerySelectableCard';
import { GallerySmartSearchRelevanceIndicator } from '~/components/v2/components/shared/gallery/GallerySmartSearchRelevanceIndicator';
import { useVideoScrubbableThumbnail } from '~/hooks/useVideoScrubbableThumbnail';
import {
  isTitleAndMetadataVisibleSelector,
  visibleCustomFieldsViewOptionsSelector,
} from '~/store/configViews/selectors';
import { type SelectableGalleryAssetItem } from '~/store/selectedItems/types';
import { getCanvasImportPreview } from '~/utils/canvasImportPreviewCache';
import { useAirSelector } from '~/utils/ReduxUtils';
import { getUploadPreview } from '~/utils/uploadPreviewCache';
import { clipSupportsStaticThumbnail } from '~/utils/videoThumbnails';

export type GalleryAssetProps<C extends Clip | PublicClip> = Pick<
  GallerySelectableCardProps<SelectableGalleryAssetItem>,
  'getItemMenuOptions' | 'getSelectionMenuOptions' | 'isSelectable'
> & {
  allCustomFields: BaseCustomField[] | undefined;
  canViewCustomFields: boolean;
  clip: C;
  height: number;
  onAssetClick: () => void;
  onAssetTitleCmdClick?: () => void;
  onCustomFieldsClick: () => void;
  onDiscussionsClick: () => void;
  onVersionsClick: () => void;
  showVersionsIndicator: boolean;
  shouldShowDiscussions: boolean;
  width: number;
};

const _GalleryAsset = <C extends Clip | PublicClip>({
  allCustomFields = [],
  canViewCustomFields,
  clip,
  getItemMenuOptions,
  getSelectionMenuOptions,
  height,
  isSelectable,
  onAssetClick,
  onAssetTitleCmdClick,
  onCustomFieldsClick,
  onDiscussionsClick,
  onVersionsClick,
  showVersionsIndicator,
  shouldShowDiscussions,
  width,
}: GalleryAssetProps<C>) => {
  const [hasCanvasImportPreview, setHasCanvasImportPreview] = useState(
    () => !!getCanvasImportPreview(clip.importedName),
  );
  const [hasUploadPreview, setHasUploadPreview] = useState(() => !!getUploadPreview(clip.importedName));

  const handlePreviewReleased = useCallback(() => {
    setHasCanvasImportPreview(false);
    setHasUploadPreview(false);
  }, []);

  const hasCachedPreview = hasCanvasImportPreview || hasUploadPreview;

  // When the spriteSheet becomes available, the clip card switches to VideoScrubbableThumbnail/
  // VideoStaticThumbnail — neither calls onPreviewReleased, so clear the shimmer here instead.
  useEffect(() => {
    if (clip.assets.spriteSheet?.src) {
      handlePreviewReleased();
    }
  }, [clip.assets.spriteSheet?.src, handlePreviewReleased]);

  const isTitleAndMetadataVisible = useAirSelector(isTitleAndMetadataVisibleSelector);
  const visibleCustomFields = useAirSelector(visibleCustomFieldsViewOptionsSelector);

  const { backgroundImageRef, backgroundImageStyles, indicatorRef, onMouseLeave, onMouseMove } =
    useVideoScrubbableThumbnail({
      spritesheet: clip.assets.spriteSheet,
      containerDimensions: {
        height,
        width,
      },
    });

  const customFields = useMemo(
    () => getAssetCustomFields({ allCustomFields: allCustomFields || [], clip, visibleCustomFields }),
    [allCustomFields, clip, visibleCustomFields],
  );

  const shouldShowCustomFields = canViewCustomFields && customFields.length > 0;

  const clipDimensions = useMemo(() => {
    const aspectRatio = clip.width / clip.height;

    return {
      width,
      height: width / aspectRatio,
    };
  }, [width, clip.height, clip.width]);

  const item = useMemo<SelectableGalleryAssetItem>(
    () => ({
      id: clip.id,
      item: clip,
      type: GalleryItemType.asset,
    }),
    [clip],
  );

  const meta = useMemo(() => {
    const meta = [];

    if (clip.ext) {
      meta.push(toUpper(clip.ext));
    }

    if (clip.size) {
      meta.push(formatBytes(clip.size));
    }

    if (clip.pages) {
      meta.push(pluralize('page', clip.pages, true));
    }

    if (!isPdfRenderedExtension(clip.ext) && !!clip.width && !!clip.height) {
      meta.push(`${clip.width} x ${clip.height}`);
    }

    return meta.join(' · ');
  }, [clip.ext, clip.height, clip.pages, clip.size, clip.width]);

  const popoverCustomFields = useMemo(
    () =>
      clip?.customFields?.filter((customField) => {
        return allCustomFields.some((cf) => cf.id === customField.id);
      }),
    [clip, allCustomFields],
  );

  const onTitleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if ((event.metaKey || event.ctrlKey) && !!onAssetTitleCmdClick) {
        onAssetTitleCmdClick?.();
      } else {
        onAssetClick?.();
      }
    },
    [onAssetClick, onAssetTitleCmdClick],
  );

  return (
    <GallerySelectableCard
      data-testid="ASSET_CARD"
      className="flex size-full flex-col rounded-16 ring-2 ring-transparent ring-inset transition-colors data-[hovering=true]:bg-grey-4 data-[selected=true]:bg-grey-4 data-[highlighted=true]:ring-blue-9 data-[selected=true]:ring-blue-9"
      getItemMenuOptions={getItemMenuOptions}
      getSelectionMenuOptions={getSelectionMenuOptions}
      isSelectable={isSelectable}
      item={item}
      onClick={onAssetClick}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      data-clip-state={clip.status}
    >
      {({ isHovering, setIsMenuOpen }) => {
        let thumbnail: ReactElement;

        if (clip.assets.spriteSheet?.src && clip.type === ClipType.video && isHovering) {
          thumbnail = (
            <VideoScrubbableThumbnail
              backgroundImageRef={backgroundImageRef}
              indicatorRef={indicatorRef}
              src={clip.assets.spriteSheet.src}
              style={backgroundImageStyles}
            />
          );
        } else if (clipSupportsStaticThumbnail(clip)) {
          thumbnail = (
            <VideoStaticThumbnail
              clip={clip}
              containerDimensions={{
                height,
                width,
              }}
            />
          );
        } else {
          thumbnail = (
            <GalleryAssetThumbnail
              clip={clip}
              isHovering={isHovering}
              width={clipDimensions.width}
              height={clipDimensions.height}
              onPreviewReleased={handlePreviewReleased}
            />
          );
        }

        return (
          <>
            <div className="pointer-events-none relative grow">
              <div
                className={cn(
                  'absolute inset-1 flex overflow-hidden rounded-12',
                  hasCachedPreview && !ClipUtils.isProcessing(clip.status) && 'bg-grey-4',
                )}
              >
                {thumbnail}
              </div>

              {hasCachedPreview && !ClipUtils.isProcessing(clip.status) && !clip.assets.spriteSheet?.src && (
                <div className="shimmer-image-overlay absolute inset-0 rounded-12" />
              )}

              <GalleryAssetShortInfo clip={clip} />

              <div className="absolute inset-1 flex flex-col justify-between overflow-hidden rounded-12">
                <div className="flex items-center justify-between p-2">
                  <div className="flex min-h-6 items-center gap-1">
                    {hasCachedPreview && ClipUtils.isProcessing(clip.status) && !clip.assets.spriteSheet?.src && (
                      <div className="flex h-6 items-center gap-1 rounded-4 bg-black/70 px-1 backdrop-blur-sm">
                        <Spinner className="size-3.5 text-white" />
                        <span className="font-semibold text-12 text-white">Processing…</span>
                      </div>
                    )}
                    <GallerySmartSearchRelevanceIndicator clip={clip} />
                    {!!popoverCustomFields?.length && canViewCustomFields && (
                      <GalleryCardCustomFieldsAction customFields={popoverCustomFields} onClick={onCustomFieldsClick} />
                    )}
                    {showVersionsIndicator && !!onVersionsClick && (
                      <CardAction
                        data-testid="CARD_VERSIONS_INDICATOR"
                        icon={<Stack className="size-4" />}
                        onClick={onVersionsClick}
                      />
                    )}
                    {shouldShowDiscussions && !!onDiscussionsClick && !!clip.openCommentCount && (
                      <CardAction
                        data-testid="CARD_DISCUSSIONS_INDICATOR"
                        onClick={onDiscussionsClick}
                        icon={<Comment className="size-4" />}
                        text={clip.openCommentCount}
                      />
                    )}
                  </div>
                  <div className="pointer-events-auto flex items-center gap-1">
                    {isHovering && (
                      <GalleryAssetMenu
                        clip={clip}
                        getItemMenuOptions={getItemMenuOptions}
                        getSelectionMenuOptions={getSelectionMenuOptions}
                        setIsMenuOpen={setIsMenuOpen}
                        trigger={
                          <CardAction data-testid="ASSET_CARD_SUB_MENU_BUTTON" icon={<Ellipsis className="size-4" />} />
                        }
                      />
                    )}
                  </div>
                </div>
                {!isTitleAndMetadataVisible && (
                  <div className="flex min-h-24 flex-col items-stretch justify-end gap-0.5 bg-gradient-to-t from-black/85 to-transparent p-2 opacity-0 transition-opacity group-focus-within/gallery-selectable-card:opacity-100 group-data-[hovering=true]/gallery-selectable-card:opacity-100">
                    <GalleryAssetTitle
                      className="text-white"
                      clip={clip}
                      onClick={onTitleClick}
                      style={{
                        textShadow: '0px 0px 4px rgba(0, 0, 0, 0.40)',
                      }}
                    />
                    <p className="truncate text-12 text-white">{meta}</p>
                  </div>
                )}
              </div>
            </div>

            {(isTitleAndMetadataVisible || shouldShowCustomFields) && (
              <div className="pointer-events-none z-10 flex flex-col items-stretch gap-2 px-3 pt-1 pb-2">
                {isTitleAndMetadataVisible && (
                  <div className="flex flex-col items-stretch gap-0.5">
                    <GalleryAssetTitle clip={clip} onClick={onTitleClick} />
                    <p className="truncate text-12 text-grey-10">{meta}</p>
                  </div>
                )}
                {shouldShowCustomFields && (
                  <CustomFieldsDisplay customFields={customFields} onClick={onCustomFieldsClick} />
                )}
              </div>
            )}
          </>
        );
      }}
    </GallerySelectableCard>
  );
};

export const GalleryAsset = memo(_GalleryAsset) as typeof _GalleryAsset;
